import {
    createSelector,
    createEntityAdapter
} from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice"

const exercisesAdapter = createEntityAdapter({})

const initialState = exercisesAdapter.getInitialState()

export const exercisesApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getExercises: builder.query({
            query: () => ({
                url: '/exercises',
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },
            }),
            transformResponse: responseData => {
                const loadedExercises = responseData.map(exercise => {
                    exercise.id = exercise._id
                    return exercise
                });
                return exercisesAdapter.setAll(initialState, loadedExercises)
            },
            providesTags: (result, error, arg) => {
                if (result?.ids) {
                    return [
                        { type: 'Exercise', id: 'LIST' },
                        ...result.ids.map(id => ({ type: 'Exercise', id }))
                    ]
                } else return [{ type: 'Exercise', id: 'LIST' }]
            }
        }),
        searchExercises: builder.query({
            query: ({ title, filtertype }) => ({
                url: `/exercises/search/?title=${title}&filtertype=${filtertype}`,
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },
            }),
            transformResponse: responseData => {
                const loadedExercises = responseData.map(exercise => {
                    exercise.id = exercise._id
                    return exercise
                });
                return exercisesAdapter.setAll(initialState, loadedExercises)
            },
        }),
        getFavorites: builder.query({
            query: (userID) => ({
                url: `/exercises/favorites/${userID}`,
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                },
            }),
            transformResponse: responseData => {
                const loadedFavorites = responseData.map(favorite => {
                    favorite.id = favorite._id
                    return favorite
                });
                return exercisesAdapter.setAll(initialState, loadedFavorites)
            },
            providesTags: (result, error, arg) => {
                if (result?.ids) {
                    return [
                        { type: 'Favorite', id: 'LIST' },
                        ...result.ids.map(id => ({ type: 'Favorite', id }))
                    ]
                } else return [{ type: 'Favorite', id: 'LIST' }]
            }
        }),
        addNewExercise: builder.mutation({
            query: initialExerciseData => ({
                url: '/exercises',
                method: 'POST',
                body: {
                    ...initialExerciseData,
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Exercise', id: arg.id }
            ]
        }),
        updateExercise: builder.mutation({
            query: initialExerciseData => ({
                url: '/exercises',
                method: 'PATCH',
                body: {
                    ...initialExerciseData,
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Exercise', id: arg.id }
            ]
        }),
        deleteExercise: builder.mutation({
            query: ({ id }) => ({
                url: `/exercises`,
                method: 'DELETE',
                body: { id }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Exercise', id: arg.id }
            ]
        }),
        addNewFavorite: builder.mutation({
            query: initialData => ({
                url: '/exercises/favorites',
                method: 'POST',
                body: {
                    ...initialData,
                }
            }),
            invalidatesTags: ['Favorite'],        
        }),
        deleteFavorite: builder.mutation({
            query: initialData => ({
                url: '/exercises/favorites',
                method: 'DELETE',
                body: {
                    ...initialData,
                }
            }),
            invalidatesTags: ['Favorite'],        
        }),
    }),
})

export const {
    useGetExercisesQuery,
    useSearchExercisesQuery,
    useGetFavoritesQuery,
    useAddNewExerciseMutation,
    useUpdateExerciseMutation,
    useDeleteExerciseMutation,
    useAddNewFavoriteMutation,
    useDeleteFavoriteMutation,
} = exercisesApiSlice

// returns the query result object
export const selectExercisesResult = exercisesApiSlice.endpoints.getExercises.select()

// creates memoized selector
const selectExercisesData = createSelector(
    selectExercisesResult,
    exercisesResult => exercisesResult.data // normalized state object with ids & entities
)

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
    selectAll: selectAllexercises,
    selectById: selectExerciseById,
    selectIds: selectExerciseIds
    // Pass in a selector that returns the exercises slice of state
} = exercisesAdapter.getSelectors(state => selectExercisesData(state) ?? initialState)